import React, { useEffect, useMemo, useState } from 'react';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_COLLEGES_API = process.env.REACT_APP_ADMIN_COLLEGES_API || `${API_BASE_URL}/api/admin-app/college-profiles/`;
const ADMIN_QMANAGERS_API = process.env.REACT_APP_ADMIN_QMANAGERS_API || `${API_BASE_URL}/api/admin-app/question-manager-profiles/`;
const ADMIN_SUBMITTED_QUESTIONS_API = process.env.REACT_APP_ADMIN_SUBMITTED_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/submitted-questions/`;
const ADMIN_QUESTIONS_API = process.env.REACT_APP_ADMIN_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/`;
const ADMIN_STUDENT_RESULTS_API = process.env.REACT_APP_ADMIN_STUDENT_RESULTS_API || `${API_BASE_URL}/api/admin-app/student-results/`;

const safeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getAuthHeaders = () => {
    // Try guest_access_token first (for backward compatibility)
    let token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
    
    // If not found, try admin userDetails (from redux-based login)
    if (!token) {
        const userDetailsStr = localStorage.getItem('userDetails');
        if (userDetailsStr) {
            try {
                const userDetails = JSON.parse(userDetailsStr);
                token = userDetails.access || '';
            } catch (e) {
                // JSON parse error, token remains empty
            }
        }
    }
    
    if (!token) {
        return null;
    }

    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

const MetricCard = ({ className = '', label, value, hint }) => (
    <div className={`admin-kpi ${className}`.trim()}>
        <h4 className="admin-kpi-value">{value}</h4>
        <p className="admin-kpi-label">{label}</p>
        {hint ? <p className="admin-kpi-hint">{hint}</p> : null}
    </div>
);

const AdminDashboardPage = () => {
    const [colleges, setColleges] = useState([]);
    const [managers, setManagers] = useState([]);
    const [submitted, setSubmitted] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    const authHeaders = useMemo(() => getAuthHeaders(), []);

    useEffect(() => {
        const load = async () => {
            if (!authHeaders) {
                setLoading(false);
                return;
            }

            try {
                const [collegesRes, managersRes, submittedRes, questionsRes, resultsRes] = await Promise.all([
                    fetch(ADMIN_COLLEGES_API, { headers: authHeaders }),
                    fetch(ADMIN_QMANAGERS_API, { headers: authHeaders }),
                    fetch(ADMIN_SUBMITTED_QUESTIONS_API, { headers: authHeaders }),
                    fetch(ADMIN_QUESTIONS_API, { headers: authHeaders }),
                    fetch(ADMIN_STUDENT_RESULTS_API, { headers: authHeaders }),
                ]);

                const collegesData = collegesRes.ok ? await collegesRes.json() : [];
                const managersData = managersRes.ok ? await managersRes.json() : [];
                const submittedData = submittedRes.ok ? await submittedRes.json() : [];
                const questionsData = questionsRes.ok ? await questionsRes.json() : [];
                const resultsData = resultsRes.ok ? await resultsRes.json() : [];

                setColleges(Array.isArray(collegesData) ? collegesData : []);
                setManagers(Array.isArray(managersData) ? managersData : []);
                setSubmitted(Array.isArray(submittedData) ? submittedData : []);
                setQuestions(Array.isArray(questionsData) ? questionsData : []);
                setResults(Array.isArray(resultsData) ? resultsData : []);
            } catch (error) {
                setColleges([]);
                setManagers([]);
                setSubmitted([]);
                setQuestions([]);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [authHeaders]);

    const chartPoints = [36, 48, 61, 60, 44, 49, 46, 50, 51, 74, 62, 82];

    const publishedQuestions = useMemo(() => questions.filter((item) => item.status === 'published').length, [questions]);
    const draftedQuestions = useMemo(() => questions.filter((item) => item.status === 'draft').length, [questions]);
    // Use submitted array directly from ADMIN_SUBMITTED_QUESTIONS_API instead of filtering questions
    const submittedQuestions = useMemo(() => submitted.length, [submitted]);
    const averageScore = useMemo(() => {
        if (results.length === 0) {
            return 0;
        }

        const total = results.reduce((sum, row) => {
            const score = safeNumber(row.score);
            const totalMarks = safeNumber(row.total_marks);
            if (!totalMarks) {
                return sum;
            }

            return sum + ((score / totalMarks) * 100);
        }, 0);

        const validCount = results.reduce((count, row) => count + (safeNumber(row.total_marks) > 0 ? 1 : 0), 0);
        return validCount ? total / validCount : 0;
    }, [results]);

    const recentColleges = useMemo(() => [...colleges].slice(0, 8), [colleges]);

    return (
        <div className="admin-dashboard-shell">
            <div className="admin-kpi-grid">
                <MetricCard
                    className="admin-kpi-purple"
                    label="Total Colleges"
                    value={loading ? '...' : colleges.length}
                    hint="Registered college accounts"
                />
                <MetricCard
                    className="admin-kpi-orange"
                    label="Question Managers"
                    value={loading ? '...' : managers.length}
                    hint="Active manager profiles"
                />
                <MetricCard
                    className="admin-kpi-green"
                    label="Submitted Questions"
                    value={loading ? '...' : submittedQuestions}
                    hint={`${loading ? '...' : submitted.length} total submitted questions`}
                />
                <MetricCard
                    className="admin-kpi-blue"
                    label="Average Score"
                    value={loading ? '...' : `${averageScore.toFixed(1)}%`}
                    hint="Based on submitted student results"
                />
            </div>

            {/* Project Created chart removed per request */}

            <div className="admin-panel-card admin-table-section">
                <h3>Recent Colleges</h3>
                <p className="admin-subtitle">Latest college entries from the live backend.</p>
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>College Name</th>
                                <th>College Code</th>
                                <th>Auto Approve</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4">Loading...</td>
                                </tr>
                            ) : recentColleges.length === 0 ? (
                                <tr>
                                    <td colSpan="4">No college records found.</td>
                                </tr>
                            ) : (
                                recentColleges.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.college_name || '-'}</td>
                                        <td>{item.college_id || '-'}</td>
                                        <td>{item.auto_approve_students ? 'Enabled' : 'Disabled'}</td>
                                        <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
